import { useRouter } from '@abyss/web/hooks/useRouter';
import { config } from '@abyss/web/tools/config';
import { storage } from '@abyss/web/tools/storage';
import { Box } from '@abyss/web/ui/Box';
import { Flex } from '@abyss/web/ui/Flex';
import { V2Footer } from '@abyss/web/ui/Footer';
import { SubFooterLink } from '@abyss/web/ui/Footer/V2Footer.types';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  extractZipCodeFromPlaceName,
  getCoverageTypeString,
  getGuestPlanSelectionData,
  getYearIndex,
} from '../../frontends/GuestExperience/Guest.utils';
import {
  getClientPortalConfig,
  getGuestThemeColors,
} from '../../frontends/GuestExperience/utils';
import { getLanguage } from '../../frontends/ProviderSearch/context/Internationalization/helpers';
import { useFpcGuestToken } from '../../hooks/useFpcGuestToken';
import { useGeoLocationStorage } from '../../hooks/useGeoLocationStorage';
import { useLagoon } from '../../hooks/useLagoon';
import { useUserLocation } from '../../hooks/useUserLocation';
import { ClientNames, Constants } from '../Constants';
import { ConstantsLagoon } from '../ConstantsLagoon';
import { getFeatureFlag } from '../Utils/getFeatureFlag';
import { FooterColumn, getFooterData } from './utils';

export const GuestFooter = () => {
  const { navigate } = useRouter();

  // Feature flag: Lagoon > psx-web-transperfect-guest-plan-selection > feature-flags > PRINTDIRECTORYFEATURE
  const lagoonFeatureFlagsTable = useLagoon(
    Constants.LAGOON_TABLE.FEATURE_FLAGS
  );
  const featureFlags: { key: string; active: boolean }[] =
    typeof lagoonFeatureFlagsTable === 'function'
      ? lagoonFeatureFlagsTable()
      : lagoonFeatureFlagsTable || [];
  const isPrintDirectoryEnabled = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.PRINTDIRECTORYFEATURE
  ); // TODO: remove after verifying Lagoon cache is refreshed

  const [section, setSection] = useState<FooterColumn[]>([]);
  const [subFooterLinks, setSubFooterLinks] = useState<SubFooterLink[]>([]);
  const configuration = useLagoon('footer-configuration')();
  const fpcConfiguration = useLagoon('fpc-token-config')();
  const FpcTokenSearchDirectoryTypes = [
    ...new Set(fpcConfiguration?.map((row: any) => row.searchDirectory) || []),
  ];
  const configurationKey = JSON.stringify(configuration);
  const { t } = useTranslation();
  const sectionLabel = t('SECTION');
  const generalLabel = t('GENERAL');
  const { selectedPlan = {}, coverageTypeCode: coverageType = '' } =
    getGuestPlanSelectionData();
  const { planYear = '' } =
    storage.session.get(Constants.STORAGE_KEYS.SESSION.GUEST_USER) || {};

  const clientName = sessionStorage.getItem(
    Constants.STORAGE_KEYS.SESSION.CLIENT_NAME
  );

  // GEHA theme detection using centralized config
  const clientPortalConfig = getClientPortalConfig(clientName);
  const theme = getGuestThemeColors(clientPortalConfig.themeOverride);
  const footerBgColor = theme.primaryColor;

  const [, getFpcGuestToken] = useFpcGuestToken({});
  const { stateCode, zipcode, longitude, latitude } = useGeoLocationStorage();
  const fipscode = stateCode || sessionStorage.getItem('countyFipsCode') || '';
  const url = window.location.href;
  const showPrint = url.includes('/guest') && url.includes('/browse');
  const FPC_BASE_URL = config('FPC_BASE_URL');

  const [userLocation, getUserLocation] = useUserLocation({});

  const navigateToFpcGuest = async () => {
    let urlZipCode = zipcode;
    const language = getLanguage()?.code;
    const yearIndex = getYearIndex(selectedPlan?.years, planYear);
    const yearObj = selectedPlan?.years?.[yearIndex];

    const planIdentifier =
      typeof selectedPlan?.planIdentifier === 'string'
        ? selectedPlan?.planIdentifier.replace(/-/g, '')
        : '';
    if (!urlZipCode) {
      await getUserLocation({
        variables: {
          longitude,
          latitude,
        },
      });
      const placeName =
        userLocation?.data?.userLocation?.features?.[0]?.place_name;
      urlZipCode = extractZipCodeFromPlaceName(placeName) ?? '';
    }

    const isLagoonDriven =
      FpcTokenSearchDirectoryTypes.includes(selectedPlan?.searchDirectory) &&
      fpcConfiguration?.find(
        (row: any) =>
          row.searchDirectory === selectedPlan?.searchDirectory &&
          row.planIdentifier.split(',').includes(planIdentifier)
      );
    const token = await getFpcGuestToken({
      variables: {
        planName: selectedPlan?.planName,
        delsys: isLagoonDriven ? isLagoonDriven.fpcDelsysCode : planIdentifier,
        coverageType: getCoverageTypeString(coverageType),
        language: language || 'en',
        clusterKey: yearObj?.clusterKey || '',
        fipsCode: fipscode,
        partnerId:
          clientName?.toLowerCase() === ClientNames.EMPIRE
            ? 'uhc.empire'
            : isLagoonDriven
            ? isLagoonDriven.partnerId
            : 'uhc',
      },
    }).then(
      async (response: { data: { getFpcGuestToken: { token: string } } }) =>
        response?.data?.getFpcGuestToken?.token || ''
    );

    const printUrl = `${FPC_BASE_URL}/guest/${token}?planyear=${planYear}&isDirectoryPrint=true&zipCode=${urlZipCode}`;
    window.open(printUrl, '_blank');
  };

  useEffect(() => {
    const footerData: ReturnType<typeof getFooterData> = getFooterData(
      configuration,
      sectionLabel,
      generalLabel,
      getLanguage()?.code === 'es',
      clientName || ''
    );
    setSection(footerData?.section);

    const subLinks: SubFooterLink[] = footerData?.general?.[0]?.links.map(
      (link) => ({
        label: link?.name,
        href: link?.url || '#',
      })
    );

    setSubFooterLinks(subLinks);
  }, [configurationKey, sectionLabel, generalLabel]);

  return (
    <Box
      css={{
        'abyss-box-root': {
          backgroundColor: '$white',
          width: '100%',
          height: 'auto',
          opacity: 1,
          fontFamily: '$primary',
          padding: '0px',
        },
      }}
    >
      <V2Footer
        css={{
          'abyss-footer-copyright': {
            display: 'none',
          },
          'abyss-footer-sub-footnotes': {
            justifyContent: 'flex-start',
            display: 'block',
          },
          'abyss-footer-sub-link': {
            fontSize: '12px',
            color: 'white !important',
          },
          'abyss-footer-container': {
            alignItems: 'center',
            padding: '24px 16px 32px 16px',
            backgroundColor: footerBgColor,
          },
          'abyss-footer-sub-list-container': {
            justifyContent: 'flex-end',
            flexDirection: 'row',
            gap: '8px',
          },
          'abyss-footer-main-container': {
            width: '100%',
            maxWidth: '1244px',
            paddingBottom: '32px',
          },
          'abyss-footer-sub-wrapper': {
            width: '100%',
            maxWidth: '1244px',
            paddingTop: '8px',
            backgroundColor: footerBgColor,
          },
          '& .abyss-link-root': {
            color: 'white !important',
          },
        }}
        data-testid="guest-footer"
        disableDefaultProviderProps={true}
        fullWidth
        subFooter={{
          links: subFooterLinks,
        }}
      >
        <Flex css={{ width: '100%', justifyContent: 'center' }}>
          <Flex
            css={{
              maxWidth: '1244px',
              justifyContent: 'space-between',
              width: '100%',
            }}
          >
            {section?.length > 0 &&
              section.map((column, i) => (
                <V2Footer.Section
                  css={{
                    'abyss-footer-section-title': {
                      marginBottom: '8px',
                    },
                    'abyss-footer-link-item': {
                      textDecoration: 'none',
                    },
                    'abyss-footer-list-container': {
                      gap: '4px',
                    },
                    'abyss-footer-section': {
                      width: '33%',
                      '@screen < $md': {
                        marginTop: i === 0 ? '0px' : '32px',
                        width: '100%',
                      },
                    },
                  }}
                  data-testid="footer-heading"
                  key={`footer-column-${i}`}
                  title={column?.header}
                >
                  {column.links.map((link, j) => (
                    <V2Footer.Link
                      css={{
                        'abyss-footer-link': {
                          textDecoration: 'none',
                          display:
                            link.url === '/link'
                              ? showPrint
                                ? 'flex'
                                : 'none'
                              : 'flex',
                          lineHeight: 1.4,
                          color: 'white !important',
                        },
                      }}
                      data-testid="footer-links"
                      href={link.url}
                      key={`footer-link-${i}-${j}`}
                      onClick={(e) => {
                        if (link.url === '/link') {
                          e.preventDefault();
                          if (isPrintDirectoryEnabled) {
                            navigate('/print-directory');
                          } else {
                            navigateToFpcGuest();
                          }
                        }
                      }}
                      openNewWindow
                    >
                      {link.name}
                    </V2Footer.Link>
                  ))}
                </V2Footer.Section>
              ))}
          </Flex>
        </Flex>
      </V2Footer>
    </Box>
  );
};

export default GuestFooter;
